/**
 * THE TYPOGRAPHY OPERATIONS — what a font FILE tells us about itself, and the five-role scale a family
 * gets once we know.
 *
 * A user drags `Graphik-Medium.woff2` onto the kit and expects the kit to know it now has Graphik at 500.
 * Nothing in the dependencies can open a font: there is no opentype.js, no fontkit, and the browser's own
 * `FontFace` will happily load a file without ever telling you the family name inside it. So the only
 * fact available at upload time is the FILENAME, and reading a filename is inference, not measurement.
 *
 * That is why every inference here is labelled rather than asserted. {@link faceFromFilename} returns
 * where each part came from, and the panel prints `Graphik · 500 (from filename)` so the one person who
 * can correct it — the person who just chose the file — can see the guess and fix it. An unlabelled guess
 * would be worse than no guess: the kit would state a weight the deck then renders in, and the mismatch
 * would only ever show up as type that looks subtly wrong.
 *
 * The scale itself is NOT invented here. {@link buildScaleFor} is a thin composition over
 * `style/typeScale.ts`, which already owns the five roles, the contract's size bands and the snapping
 * rule that stops a deck faux-bolding a family that ships one file. Anything this module decided on its
 * own would be a second type system disagreeing with the first.
 */
import {
  deriveTypeScale,
  snapScaleWeights,
  TYPE_ROLES,
  weightsForFamily,
  type TypeRole,
  type TypeScale,
} from '../style/typeScale'

/** The shape a kit stores a font file in — `brandStyle.fontFaces`, which is what `mirrorKitFonts` writes
 *  and what `weightsForFamily` reads. Restated structurally rather than imported so this module depends
 *  on the type system and not on the site capture. */
export interface KitFontFace {
  family: string
  /** `'400'`, `'bold'`, or absent. The same loose shape `@font-face` allows. */
  weight?: string
  style?: string
  src?: string
}

/* ── reading a filename ──────────────────────────────────────────────────────────────────────────── */

/**
 * The weight words a foundry actually ships, and what each one means numerically.
 *
 * Ordered longest-first at match time, because `extrabold` contains `bold` and `semibold` contains
 * `bold`: matching the short one first turns every SemiBold file into a 700.
 */
const WEIGHT_WORDS: Record<string, number> = {
  hairline: 100,
  thin: 100,
  extralight: 200,
  ultralight: 200,
  light: 300,
  book: 400,
  normal: 400,
  regular: 400,
  roman: 400,
  medium: 500,
  demibold: 600,
  semibold: 600,
  bold: 700,
  extrabold: 800,
  ultrabold: 800,
  black: 900,
  heavy: 900,
}

/** Longest first, so `extrabold` is tried before the `bold` inside it. */
const WEIGHT_WORD_KEYS = Object.keys(WEIGHT_WORDS).sort((a, b) => b.length - a.length)

/** Tokens a filename carries that are about the FILE rather than about the family. Dropped from the
 *  family name, and none of them says anything about weight. */
const NOISE_TOKENS = new Set([
  'variablefont',
  'variable',
  'vf',
  'wght',
  'opsz',
  'webfont',
  'web',
  'subset',
  'latin',
  'latinext',
  'font',
  'trial',
  'test',
  'copy',
])

/** Noise anywhere in the name, not just at the end: `Inter_18pt-600` is Inter at 600, and the optical
 *  size is a fact about the FILE. */
const isNoise = (lower: string): boolean => NOISE_TOKENS.has(lower) || /^\d+pt$/.test(lower)

/**
 * `bold` → 700, from the SAME vocabulary a filename is read with.
 *
 * A user says "set the heading to medium" as readily as a foundry names a file `Graphik-Medium.woff2`,
 * and the two have to mean one number. Exported rather than duplicated in the agent host, because a
 * second table would be the place the two quietly disagree — a chat asking for semibold landing on a 700
 * while the uploaded SemiBold file registers as a 600.
 */
export function weightFromWord(word: string): number | null {
  const key = word.trim().toLowerCase().replace(/[\s_-]+/g, '')
  return WEIGHT_WORDS[key] ?? null
}

export interface InferredFace {
  /** `Graphik`. Whatever survives after the weight, the style and the file noise are removed. */
  family: string
  weight: number
  style: 'normal' | 'italic'
  /** True when the filename actually NAMED a weight. False means `weight` is the 400 default, which is a
   *  guess about a guess and the one the panel most wants to offer for correction. */
  weightNamed: boolean
  /** True when the filename said italic or oblique. */
  styleNamed: boolean
  /** Always `filename`. There is no font parser in the dependencies; see the module note. */
  source: 'filename'
}

/** Split on every separator a font filename uses, and on the camel-case humps of `GraphikMedium`. The
 *  first rule is the acronym one — `SFProDisplay` and `IBMPlexSans` have no lowercase-to-uppercase hump
 *  where the acronym ends, so without it the family comes out as `SFPro`. */
function tokenize(stem: string): string[] {
  return stem
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .split(/[\s._\-+]+/)
    .filter(Boolean)
}

/** `graphik` → `Graphik`, `iA` → `iA`. Only an all-lowercase token is touched: anything with a capital in
 *  it was already spelled by a human who cared. */
const titled = (token: string): string => (/^[a-z]/.test(token) && token === token.toLowerCase()
  ? token[0].toUpperCase() + token.slice(1)
  : token)

/**
 * `Graphik-Medium.woff2` → `{ family: 'Graphik', weight: 500 }`.
 *
 * READ FROM THE RIGHT, and that is the whole trick. Every foundry writes the modifiers last — family,
 * then weight, then style — so the family is not "the tokens that are not weight words", it is
 * "everything to the left of the trailing run of modifiers". Scanning left to right instead is what turns
 * `TimesNewRoman-Bold` into the family `Times New`, and `Book Antiqua` into a 400 called Antiqua: both
 * names contain a weight word that is part of the NAME.
 *
 * The scan takes at most ONE weight, because a file describes one. A second weight word further left is
 * the family's own word and terminates the scan. Style and file noise are consumed freely on either side
 * of it, which is what makes `Inter-Italic-Bold` and `Inter-VariableFont_wght` both read correctly.
 *
 * The four shapes that actually arrive: the separator form (`Graphik-Medium`), the glued form
 * (`GraphikMedium`, and `graphikmedium` where the camel split cannot fire), the numeric form
 * (`Graphik-500`, `Inter_18pt-600`) and the variable form, which names no weight at all and gets the 400
 * default with `weightNamed: false`.
 */
export function faceFromFilename(name: string): InferredFace {
  const stem = name.replace(/\.(woff2?|ttf|otf|eot)$/i, '')
  const tokens = tokenize(stem)

  let weight = 400
  let weightNamed = false
  let style: 'normal' | 'italic' = 'normal'
  let styleNamed = false

  let cut = tokens.length - 1
  while (cut >= 0) {
    const lower = tokens[cut].toLowerCase()

    if (lower === 'italic' || lower === 'oblique') {
      style = 'italic'
      styleNamed = true
      cut -= 1
      continue
    }
    // Axis and packaging labels, plus the optical size a foundry writes as `18pt`. Neither is a weight
    // and neither is part of the family's name.
    if (isNoise(lower)) {
      cut -= 1
      continue
    }
    if (!weightNamed) {
      // A bare number on the CSS weight ladder. `400` yes; `18` no, and not the `2` of `woff2` either,
      // which the extension strip already removed.
      if (/^[1-9]00$/.test(lower)) {
        weight = Number(lower)
        weightNamed = true
        cut -= 1
        continue
      }
      // Two tokens at a time first: the camel split turns `SemiBold` into `Semi` + `Bold`, and matching
      // the right-hand one alone makes every SemiBold file a 700.
      const pair = cut > 0 ? tokens[cut - 1].toLowerCase() + lower : ''
      if (pair && WEIGHT_WORDS[pair] !== undefined) {
        weight = WEIGHT_WORDS[pair]
        weightNamed = true
        cut -= 2
        continue
      }
      if (WEIGHT_WORDS[lower] !== undefined) {
        weight = WEIGHT_WORDS[lower]
        weightNamed = true
        cut -= 1
        continue
      }
    }
    break
  }

  const familyTokens = tokens.slice(0, cut + 1).filter((t) => !isNoise(t.toLowerCase()))

  // The glued form the camel split could not reach, because the file arrived as `graphikmedium`. Only a
  // SUFFIX counts, and only when something is left in front of it: `Bookman` ends in no weight word at
  // all, and `Book` on its own is a name here rather than a 400.
  if (!weightNamed && familyTokens.length) {
    const last = familyTokens[familyTokens.length - 1].toLowerCase()
    const suffix = WEIGHT_WORD_KEYS.find((w) => last.length > w.length && last.endsWith(w))
    if (suffix) {
      weight = WEIGHT_WORDS[suffix]
      weightNamed = true
      familyTokens[familyTokens.length - 1] = familyTokens[familyTokens.length - 1].slice(0, last.length - suffix.length)
    }
  }

  // Nothing left means the whole filename was a modifier (`Medium.woff2`), and the file's own name is
  // still a better label than an empty field the user cannot tell apart from a failure.
  const family = familyTokens.map(titled).join(' ').trim() || titled(tokens[tokens.length - 1] ?? '') || stem

  return { family, weight, style, weightNamed, styleNamed, source: 'filename' }
}

/** The label the panel prints next to an uploaded file: `Graphik · 500 (from filename)`. The parenthesis
 *  is the invitation to correct it, and it is never dropped — every fact in an {@link InferredFace} came
 *  from the filename, including the ones the filename did not state. */
export function faceLabel(face: InferredFace): string {
  const style = face.style === 'italic' ? ' italic' : ''
  return `${face.family} · ${face.weight}${style} (from filename)`
}

/* ── building the scale ──────────────────────────────────────────────────────────────────────────── */

/**
 * The five-role scale for a family the kit now owns.
 *
 * Composition, not arithmetic: `deriveTypeScale` produces the slide-scale anchor (mid-band for every
 * role, inside the ranges ARTIFACT_CONTRACT already enforces) and then snaps every role's weight onto a
 * weight that family actually SHIPS. That snap is the whole reason this function takes the face list
 * rather than the family name alone — a kit that asks for a 700 heading from a family shipping one 400
 * file gets a browser-faked bold on every slide, which is the failure `snapScaleWeights` exists for.
 *
 * `bodyFamily` is separate because the two faces are separate files with separate weight lists. Omit it
 * and the display family carries both, which is what a kit with one uploaded font actually has.
 */
export function buildScaleFor(
  family: string,
  faces: readonly KitFontFace[] | null | undefined,
  bodyFamily?: string | null,
): TypeScale {
  return deriveTypeScale({
    displayWeights: weightsForFamily(faces, family),
    bodyWeights: weightsForFamily(faces, bodyFamily || family),
  })
}

/** An existing scale re-snapped after the kit's font files changed. The scale the user tuned is kept;
 *  only weights the new files cannot render move. */
export function resnapScale(
  scale: TypeScale,
  family: string,
  faces: readonly KitFontFace[] | null | undefined,
  bodyFamily?: string | null,
): TypeScale {
  return snapScaleWeights(scale, {
    displayWeights: weightsForFamily(faces, family),
    bodyWeights: weightsForFamily(faces, bodyFamily || family),
  })
}

/**
 * The words a user reaches for, mapped onto the five roles that exist.
 *
 * `TYPE_ROLES` is a CLOSED tuple: generation reads exactly five roles, so a sixth one stored in a kit is
 * a field nothing renders. But "make the paragraph bigger" and "set the eyebrow in the display face" are
 * both askable and both mean one of the five, so a bare `includes` check would refuse work the kit can
 * actually do. Returns null only for a role that genuinely does not exist, which is where the caller
 * refuses and offers a usage rule instead.
 */
const ROLE_ALIASES: Record<string, TypeRole> = {
  title: 'display',
  hero: 'display',
  cover: 'display',
  h1: 'heading',
  headline: 'heading',
  head: 'heading',
  h2: 'subheading',
  subhead: 'subheading',
  eyebrow: 'subheading',
  kicker: 'subheading',
  label: 'caption',
  paragraph: 'body',
  prose: 'body',
  text: 'body',
  'small paragraph': 'caption',
  small: 'caption',
  footnote: 'caption',
  source: 'caption',
}

export function resolveTypeRole(name: string): TypeRole | null {
  const key = name.trim().toLowerCase().replace(/\s+/g, ' ')
  const exact = TYPE_ROLES.find((role) => role === key)
  return exact ?? ROLE_ALIASES[key] ?? null
}
